import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';

interface FieldChangeEvent {
  fieldKey: string;
  value: string;
}

interface TemperatureChangeEvent {
  oldTemperature: number;
  value: string;
}

interface RheologyRowView {
  key: string;
  label: string;
  type: 'number';
  min?: number;
  step?: number;
}

@Component({
  selector: 'app-job-request-test-rheology-detail',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule],
  templateUrl: './job-request-test-rheology-detail.component.html',
  styleUrl: './job-request-test-rheology-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class JobRequestTestRheologyDetailComponent {
  @Input({ required: true }) testKey = '';
  @Input() temperatures: number[] = [];
  @Input() rheologyRows: RheologyRowView[] = [];
  @Input({ required: true }) gridTemplateColumns = '';
  @Input({ required: true }) fieldValueByKey!: (testKey: string, fieldKey: string) => string;
  @Input({ required: true }) rheologyFieldKey!: (temperature: number, rowKey: string) => string;
  @Input({ required: true }) plasticViscosity!: (testKey: string, temperature: number) => string;
  @Input({ required: true }) yieldPoint!: (testKey: string, temperature: number) => string;

  @Output() fieldChanged = new EventEmitter<FieldChangeEvent>();
  @Output() addTemperature = new EventEmitter<void>();
  @Output() removeTemperature = new EventEmitter<number>();
  @Output() temperatureChanged = new EventEmitter<TemperatureChangeEvent>();

  protected onFieldInput(fieldKey: string, value: string): void {
    this.fieldChanged.emit({ fieldKey, value });
  }

  protected onReadingEnter(event: KeyboardEvent, rowIndex: number, temperatureIndex: number): void {
    event.preventDefault();
    event.stopPropagation();

    const nextRowIndex = rowIndex + 1;
    const hasNextRow = nextRowIndex < this.rheologyRows.length;
    const nextTemperatureIndex = hasNextRow ? temperatureIndex : temperatureIndex + 1;
    const targetRowIndex = hasNextRow ? nextRowIndex : 0;
    const nextInput = (event.currentTarget as HTMLInputElement)
      .closest('.rheology-matrix')
      ?.querySelector<HTMLInputElement>(
        `input[data-rheology-column="${nextTemperatureIndex}"][data-rheology-row="${targetRowIndex}"]`
      );

    nextInput?.focus();
  }

  protected onTemperatureChange(oldTemperature: number, value: string): void {
    this.temperatureChanged.emit({ oldTemperature, value });
  }

  protected isF2Spring(temperature: number): boolean {
    return this.fieldValueByKey(this.testKey, this.rheologyFieldKey(temperature, 'f2Spring')).toLowerCase() === 'true';
  }

  protected onF2SpringChange(temperature: number, checked: boolean): void {
    this.onFieldInput(this.rheologyFieldKey(temperature, 'f2Spring'), checked ? 'true' : 'false');
  }

  protected onF2SpringClick(temperature: number, event: Event): void {
    event.preventDefault();
    this.onF2SpringChange(temperature, !this.isF2Spring(temperature));
  }

  protected isHighReading(temperature: number, rowKey: string): boolean {
    if (this.isF2Spring(temperature)) {
      return false;
    }

    const rawValue = this.fieldValueByKey(this.testKey, this.rheologyFieldKey(temperature, rowKey)).trim();
    if (!rawValue) {
      return false;
    }

    const value = Number(rawValue);
    return Number.isFinite(value) && value >= 310;
  }

  protected doubledValue(value: string): string {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return '';
    }

    const parsedValue = Number(trimmedValue);
    return Number.isFinite(parsedValue) ? String(parsedValue * 2) : '';
  }
}
